import { useEffect } from "react";
import { useFormikContext } from "formik";
import { CampaignCreationSteps } from "../campaign-constants";
import {
  adFormatStepSchema,
  budgetStepSchema,
  brandStepSchema,
  marketsMerchantsStepSchema,
  placementStepSchema,
  productsStepSchema,
  type CampaignFormValues,
} from "../campaign-form";
import { AdFormatStep } from "../steps/ad-format-step";
import { BrandStep } from "../steps/brand-step";
import { type ZodSafeParseResult } from "zod";
import { MarketsMerchantsStep } from "../steps/markets-merchants-step";
import { PlacementStep } from "../steps/placement-step";
import { BudgetStep } from "../steps/budget-step";
import { ProductsStep } from "../steps/products-step";
import { ReviewLaunchStep } from "../steps/review-launch-step";

type StepRendererProps = {
  steps: readonly string[];
  currentStep: number;
  setStepState: (state: {
    canProceed: boolean;
    errorMessage?: string;
    errorField?: string;
  }) => void;
  showError: boolean;
  errorMessage?: string;
  errorField?: string;
  onDismissError: () => void;
  showLoadingSelectedBrand?: boolean;
  isSubmitting?: boolean;
  submitErrorMessage?: string;
};

function getStepStateFromResult(
  result: ZodSafeParseResult<CampaignFormValues>,
) {
  return result.success
    ? { canProceed: true as const }
    : {
        canProceed: false as const,
        errorMessage: result.error.issues[0]?.message,
        errorField: result.error.issues[0]?.path[0]?.toString(),
      };
}

function PlaceholderStep({
  step,
  steps,
}: {
  step: number;
  steps: readonly string[];
}) {
  return (
    <section className="tw:max-w-4xl">
      <header className="tw:space-y-3">
        <h1 className="tw:text-3xl tw:font-semibold tw:tracking-tight tw:text-foreground">
          {steps[step - 1]}
        </h1>
        <p className="tw:max-w-3xl tw:text-lg tw:text-muted-foreground">
          This step is ready for implementation next. The multistep flow is now
          wired, and the selected brand carries forward from step 1.
        </p>
      </header>
    </section>
  );
}

export function StepRenderer({
  steps,
  currentStep,
  setStepState,
  showError,
  errorMessage,
  errorField,
  onDismissError,
  showLoadingSelectedBrand = false,
  isSubmitting = false,
  submitErrorMessage,
}: StepRendererProps) {
  const { values } = useFormikContext<CampaignFormValues>();

  useEffect(() => {
    switch (currentStep) {
      case CampaignCreationSteps.BRAND: {
        const result = brandStepSchema.safeParse(values);

        setStepState(getStepStateFromResult(result));
        break;
      }
      case CampaignCreationSteps.PRODUCTS: {
        const result = productsStepSchema.safeParse(values);

        setStepState(getStepStateFromResult(result));
        break;
      }
      case CampaignCreationSteps.AD_FORMAT: {
        const result = adFormatStepSchema.safeParse(values);

        setStepState(getStepStateFromResult(result));
        break;
      }
      case CampaignCreationSteps.MARKETS_AND_MERCHANTS: {
        const result = marketsMerchantsStepSchema.safeParse(values);

        setStepState(getStepStateFromResult(result));
        break;
      }
      case CampaignCreationSteps.PLACEMENT: {
        const result = placementStepSchema.safeParse(values);

        setStepState(getStepStateFromResult(result));
        break;
      }
      case CampaignCreationSteps.BUDGET: {
        const result = budgetStepSchema.safeParse(values);

        setStepState(getStepStateFromResult(result));
        break;
      }
      default:
        setStepState({ canProceed: true });
        break;
    }
  }, [currentStep, setStepState, values]);

  if (currentStep === CampaignCreationSteps.BRAND) {
    return (
      <BrandStep
        showError={showError}
        errorMessage={errorMessage}
        errorField={errorField}
        showLoadingSelectedBrand={showLoadingSelectedBrand}
        onDismissError={onDismissError}
      />
    );
  }

  if (currentStep === CampaignCreationSteps.PRODUCTS) {
    return (
      <ProductsStep
        showError={showError}
        errorMessage={errorMessage}
        errorField={errorField}
        onDismissError={onDismissError}
      />
    );
  }

  if (currentStep === CampaignCreationSteps.AD_FORMAT) {
    return (
      <AdFormatStep
        showError={showError}
        errorMessage={errorMessage}
        errorField={errorField}
        onDismissError={onDismissError}
      />
    );
  }

  if (currentStep === CampaignCreationSteps.MARKETS_AND_MERCHANTS) {
    return (
      <MarketsMerchantsStep
        showError={showError}
        errorMessage={errorMessage}
        errorField={errorField}
        onDismissError={onDismissError}
      />
    );
  }

  if (currentStep === CampaignCreationSteps.PLACEMENT) {
    return (
      <PlacementStep
        showError={showError}
        errorMessage={errorMessage}
        errorField={errorField}
        onDismissError={onDismissError}
      />
    );
  }

  if (currentStep === CampaignCreationSteps.BUDGET) {
    return (
      <BudgetStep
        showError={showError}
        errorMessage={errorMessage}
        errorField={errorField}
        onDismissError={onDismissError}
      />
    );
  }

  if (currentStep === CampaignCreationSteps.REVIEW_AND_LAUNCH) {
    return (
      <ReviewLaunchStep
        isSubmitting={isSubmitting}
        submitErrorMessage={submitErrorMessage}
      />
    );
  }

  return <PlaceholderStep step={currentStep} steps={steps} />;
}
